// The knowledge graph in the transcript: the investigation as it stood after
// a call, drawn as the footer panel draws it (GraphTimeline) from the live
// graph of the chat on screen, with the call's additions marked; and the text
// the model reads behind a small toggle. A call the live graph does not hold
// (another chat's, or a graph since cleared) falls back to the slice its
// snapshot kept, as `relations` and `rows` blocks. GraphDeltaMarker is the
// one-line summary that sits on a tool row which moved the graph.
import React, { useMemo, useState, useSyncExternalStore } from "react";

import ExportableBlock from "./ExportableBlock.jsx";
import GraphTimeline, { Glyph } from "./GraphTimeline.jsx";
import HoverTooltip from "./HoverTooltip.jsx";
import OverflowTooltip from "./OverflowTooltip.jsx";
import { openGraphCanvas } from "./InvestigationGraphPill.jsx";
import { KernelBlock } from "./KernelPrimitives.jsx";
import { stepForCall } from "../lib/graphTimeline.js";
import { activeGraphKey, graphVersion, readLiveGraph, subscribeGraph } from "../lib/investigationGraphRun.js";
import { VIEW_KIND_ORDER, claimDisplayRows, claimRows, countKinds, pluralKind, relationsData, relationsSpec } from "../lib/investigationGraphView.js";
import { claimTraceSession, parseGraphFocus } from "../lib/traceGraphOverlay.js";

export function addedText(snapshot) {
    const counts = snapshot?.addedByKind && Object.keys(snapshot.addedByKind).length ? snapshot.addedByKind : countKinds(snapshot?.newKeys || []);
    const parts = VIEW_KIND_ORDER.filter((k) => counts[k]).map((k) => `${counts[k]} ${pluralKind(k, counts[k])}`);
    const added = typeof snapshot?.added === "number" ? snapshot.added : (snapshot?.newKeys || []).length;
    if (!added) return "claim recorded";
    return `+${added} ${parts.join(", ")}`;
}

// The kinds the chart draws as marks on its lanes.
const MARK_KINDS = ["process", "message", "prompt"];

// A kind as the chart draws it: its marks in the graph colour the chart gives
// an addition, and the structure (machines, sessions, people, windows, hosts)
// as the rail, the chips, the bands and the host leaves draw them.
export function KindGlyph({ kind, size = 10 }) {
    const c = size / 2;
    let glyph;
    if (MARK_KINDS.includes(kind)) glyph = <Glyph kind={kind} x={c} y={c} fill="var(--graph)" />;
    else if (kind === "endpoint") glyph = <rect x={c - 3.5} y={c - 3.5} width={7} height={7} fill="var(--ink)" />;
    else if (kind === "session") glyph = <circle cx={c} cy={c} r={3.5} fill="var(--muted)" />;
    else if (kind === "person") glyph = <rect x={0.5} y={c - 3} width={size - 1} height={6} fill="none" stroke="var(--line-strong)" />;
    else if (kind === "window") glyph = <rect x={0} y={1} width={size} height={size - 2} fill="color-mix(in srgb, var(--graph) 14%, transparent)" />;
    else if (kind === "host") glyph = <rect x={c - 2.6} y={c - 2.6} width={5.2} height={5.2} transform={`rotate(45 ${c} ${c})`} fill="none" stroke="var(--muted)" strokeWidth={1.2} />;
    else return null;
    return <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="block shrink-0" aria-hidden="true">{glyph}</svg>;
}

// On the row: the marks the call added, as the chart draws an addition, up to
// eight of each kind. What it added to the structure is in the words beside.
export function MiniRibbon({ counts }) {
    const kinds = MARK_KINDS.filter((k) => counts?.[k]);
    if (!kinds.length) return null;
    let budget = 24;
    return (
        <span className="inline-flex items-center gap-[2px]" aria-hidden="true">
            {kinds.flatMap((kind) => {
                const n = Math.min(counts[kind], 8, budget);
                budget -= n;
                return Array.from({ length: n }, (_, i) => <KindGlyph key={`${kind}-${i}`} kind={kind} size={6} />);
            })}
        </span>
    );
}

// What the marker says on hover: each kind this call added, drawn as the
// chart draws it, and the running totals.
export function MarkerCard({ snapshot, counts }) {
    const kinds = VIEW_KIND_ORDER.filter((k) => counts?.[k]);
    return (
        <div className="flex flex-col gap-[4px] text-left">
            <div className="text-[12px] text-[color:var(--muted)] first-letter:uppercase">this call added</div>
            {kinds.map((kind) => (
                <div key={kind} className="flex items-center gap-[6px] text-[11px] tabular-nums text-[color:var(--ink)]">
                    <span className="grid w-[10px] place-items-center"><KindGlyph kind={kind} /></span>
                    <span>{counts[kind]} {pluralKind(kind, counts[kind])}</span>
                </div>
            ))}
            <div className="text-[10.5px] text-[color:var(--muted-soft)]">
                {snapshot.nodes} anchor{snapshot.nodes === 1 ? "" : "s"} total{snapshot.claims?.total ? `, ${snapshot.claims.supported}/${snapshot.claims.total} claims supported` : ""}. Click to open the graph.
            </div>
        </div>
    );
}

// Sits on the tool row. Hover names what the call added; click opens the
// row's Graph section directly (the parent wires `onOpen`).
export function GraphDeltaMarker({ snapshot, onOpen = null }) {
    if (!snapshot?.changed) return null;
    const counts = snapshot.addedByKind && Object.keys(snapshot.addedByKind).length ? snapshot.addedByKind : countKinds(snapshot.newKeys || []);
    const activate = (e) => {
        if (!onOpen) return;
        e.stopPropagation();
        e.preventDefault();
        onOpen();
    };
    return (
        <HoverTooltip placement="left" wide label={<MarkerCard snapshot={snapshot} counts={counts} />}>
            <span
                role={onOpen ? "button" : undefined}
                tabIndex={onOpen ? 0 : undefined}
                aria-label={onOpen ? `Open the knowledge graph after this call: ${addedText(snapshot)}` : undefined}
                onClick={onOpen ? activate : undefined}
                onKeyDown={onOpen ? (e) => { if (e.key === "Enter" || e.key === " ") activate(e); } : undefined}
                className={[
                    "ml-[6px] inline-flex min-w-0 shrink items-center gap-[5px] whitespace-nowrap text-[10.5px] tabular-nums text-[color:var(--muted-soft)]",
                    onOpen ? "cursor-pointer hover:text-[color:var(--ink)]" : "",
                ].join(" ")}
                data-graph-delta
            >
                <MiniRibbon counts={counts} />
                <span className="min-w-0 truncate">{addedText(snapshot)}</span>
            </span>
        </HoverTooltip>
    );
}

// A snapshot may hold a shortened statement (older snapshots did); when
// the live graph of the chat on screen has the same claim and its text
// extends the snapshot's, the fuller text is shown. The prefix check keeps
// a claim id from another graph from ever being substituted.
export function fullerClaims(view) {
    const rows = claimRows(view);
    let live = null;
    try { live = readLiveGraph(activeGraphKey()); } catch { live = null; }
    if (!live?.claims?.length) return rows;
    const byId = new Map(live.claims.map((c) => [c.id, c]));
    return (view?.claims || []).map((c, i) => {
        const l = byId.get(c.id);
        if (l && typeof l.statement === "string" && l.statement.length > c.statement.length && l.statement.startsWith(c.statement)) {
            return { ...rows[i], statement: l.statement };
        }
        return rows[i];
    });
}

// The live graph of the chat on screen and the step `toolCallId` left it at,
// or null when that graph does not hold the call.
function liveStep(key, snapshot, toolCallId) {
    let graph = null;
    try {
        graph = readLiveGraph(key);
    } catch {
        return null;
    }
    if (!graph?.nodes?.size && !graph?.claims?.length) return null;
    const at = stepForCall(graph, { toolCallId, keys: snapshot?.newKeys || [] });
    return at ? { key, graph, ...at } : null;
}

export function GraphBlock({ snapshot, nodeId = "graph", toolCallId = null }) {
    const [showText, setShowText] = useState(false);
    // The chat moving on renumbers the steps, and a graph that loads after the
    // block first draws brings the call into it.
    const key = activeGraphKey();
    const version = useSyncExternalStore((cb) => subscribeGraph(key, cb), () => graphVersion(key), () => 0);
    const live = useMemo(() => (snapshot?.changed ? liveStep(key, snapshot, toolCallId) : null), [key, version, snapshot, toolCallId]);
    const at = useMemo(() => (live ? { step: live.step, first: live.first, freshSteps: live.ids, fresh: new Set(snapshot.newKeys || []) } : null), [live, snapshot]);
    // A claim's door into its trace, as the footer panel offers it.
    const claimTrace = useMemo(() => {
        if (!live || !parseGraphFocus(live.key)) return null;
        const byId = new Map(live.graph.claims.map((c) => [c.id, c]));
        return (id) => (byId.has(id) ? claimTraceSession(live.graph, byId.get(id)) : null);
    }, [live]);
    if (!snapshot?.changed || !snapshot.view) return null;
    const after = `after this call: ${snapshot.nodes} anchor${snapshot.nodes === 1 ? "" : "s"}${snapshot.claims?.total ? `, ${snapshot.claims.supported}/${snapshot.claims.total} claims supported` : ""}`;
    const title = `knowledge graph ${after}`;
    const claims = live ? [] : claimDisplayRows(fullerClaims(snapshot.view));
    // A raised panel, so the picture reads as the call's, apart from the
    // thread's text around it; the timeline's chips take its surface. Framed
    // as the workspace's panels are, its title one line: the name as the
    // workspace's eyebrows are, what it holds in the panel's own type.
    return (
        <div data-graph-block className="rounded-[10px] border border-[color:var(--line)] bg-[color:var(--panel)] px-[12px] py-[10px]" style={{ "--timeline-surface": "var(--panel)" }}>
            <ExportableBlock filenamePrefix={`graph-${nodeId}`}>
                {live ? (
                    <>
                        <OverflowTooltip text={title} className="mb-[8px] block text-[11px] text-[color:var(--muted)]">
                            <span className="inline-block first-letter:uppercase">knowledge graph</span>
                            {` ${after}`}
                        </OverflowTooltip>
                        <GraphTimeline
                            graph={live.graph}
                            version={version}
                            at={at}
                            claimTrace={claimTrace}
                            onOpenTrace={(traceId) => openGraphCanvas({ graph: live.graph, graphKey: live.key, focus: traceId })}
                        />
                    </>
                ) : (
                    <>
                        <KernelBlock spec={relationsSpec(snapshot.view, { title })} data={relationsData(snapshot.view)} />
                        {claims.length ? (
                            <div className="mt-[8px]">
                                <KernelBlock spec={{ kind: "rows", primaryField: "statement", secondaryField: "kind", rightField: "status", maxRows: 12, wrap: true }} data={claims} />
                            </div>
                        ) : null}
                    </>
                )}
            </ExportableBlock>
            <button
                type="button"
                onClick={() => setShowText((v) => !v)}
                aria-pressed={showText}
                className="mt-[6px] bg-transparent text-[10.5px] text-[color:var(--muted)] hover:text-[color:var(--ink)] cursor-pointer"
            >
                {showText ? "hide the model's view" : "show the model's view"}
            </button>
            {showText ? (
                <pre className="mt-[4px] max-h-[320px] overflow-auto whitespace-pre-wrap break-words font-mono text-[10.5px] leading-[1.35] text-[color:var(--muted)]">{snapshot.render || ""}</pre>
            ) : null}
        </div>
    );
}
